import { useState, useCallback } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';

interface HasId {
  id: number;
}

export interface UseTableRowsReturn<T extends HasId> {
  data: T[];
  setData: React.Dispatch<React.SetStateAction<T[]>>;
  addRow: () => void;
  removeRow: (id: number) => void;
  handleDragEnd: (event: DragEndEvent) => void;
}

export function useTableRows<T extends HasId>(
  initialData: T[],
  createEmptyRow: () => Omit<T, 'id'>
): UseTableRowsReturn<T> {
  const [data, setData] = useState<T[]>(initialData);

  const addRow = useCallback(() => {
    setData((prev) => {
      const newId = prev.length > 0 ? Math.max(...prev.map((row) => row.id)) + 1 : 1;
      return [...prev, { ...createEmptyRow(), id: newId } as T];
    });
  }, [createEmptyRow]);

  const removeRow = useCallback((id: number) => {
    setData((prev) => {
      if (prev.length > 1) {
        return prev.filter((row) => row.id !== id);
      }
      return prev;
    });
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setData((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  return { data, setData, addRow, removeRow, handleDragEnd };
}
